<template>
  <div
    class="modal fade"
    id="formSummaryStockSignatureModal"
    tabindex="-1"
    role="dialog"
    aria-hidden="true"
    ref="modalRoot"
  >
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <!-- begin: หัว popup พื้นน้ำเงิน (design ข้อ 1) -->
        <div class="modal-header fss-sig__header">
          <h5 class="modal-title">
            <i class="fas fa-signature"></i> จัดการลายเซ็นผู้จัดทำ
            <small v-if="documentNo" class="fss-sig__header-doc">
              — เลขที่เอกสาร {{ documentNo }}
            </small>
          </h5>
          <button
            type="button"
            class="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <!-- end: หัว popup -->

        <div class="modal-body">
          <!-- begin: เตือนเมื่อระบบยังไม่มีลายเซ็นค่าเริ่มต้น -->
          <div
            v-if="isMissingDefault"
            class="alert alert-warning fss-sig__alert"
            role="alert"
          >
            <i class="fas fa-exclamation-triangle"></i>
            ระบบยังไม่มีลายเซ็นค่าเริ่มต้น เอกสารที่บันทึกตอนนี้จะพิมพ์ออกมาโดยไม่มีภาพลายเซ็น
            กรุณาตั้งค่าที่แถบ <strong>จัดการลายเซ็นค่าเริ่มต้นระบบ</strong> ก่อน
          </div>
          <!-- end: เตือนเมื่อระบบยังไม่มีลายเซ็นค่าเริ่มต้น -->

          <!-- begin: แถบ -->
          <ul class="nav nav-tabs fss-sig__tabs" role="tablist">
            <li class="nav-item">
              <a
                class="nav-link"
                :class="{ active: activeTab === 'document' }"
                href="javascript:;"
                data-test="fss-sig-tab-document"
                @click="activeTab = 'document'"
              >
                ใช้กับเอกสารนี้เท่านั้น
              </a>
            </li>
            <li class="nav-item">
              <a
                class="nav-link"
                :class="{ active: activeTab === 'default' }"
                href="javascript:;"
                data-test="fss-sig-tab-default"
                @click="activeTab = 'default'"
              >
                จัดการลายเซ็นค่าเริ่มต้นระบบ
                <!--
                  design ข้อ 2 — ป้ายเตือนติดหัวแถบ ไม่ใช่แค่ข้อความในเนื้อหา
                  เพราะผู้ใช้กดสลับแถบก่อนจะได้อ่านคำอธิบายข้างใน
                -->
                <span class="badge badge-danger fss-sig__tab-badge">
                  กระทบทั้งระบบ
                </span>
              </a>
            </li>
          </ul>
          <!-- end: แถบ -->

          <div class="tab-content fss-sig__content">
            <!-- begin: แถบที่ 1 — เฉพาะเอกสารนี้ -->
            <div v-show="activeTab === 'document'" data-test="fss-sig-pane-document">
              <!-- design ข้อ 3 — คำอธิบายแถบทำเป็นกล่องสีฟ้า ให้แยกออกจากเนื้อหาด้านล่าง -->
              <div class="alert alert-info fss-sig__notice">
                <i class="fas fa-info-circle"></i>
                ภาพที่เลือกในแถบนี้ใช้กับ<strong>เอกสารฉบับนี้เท่านั้น</strong>
                <strong>ไม่กระทบ</strong>ลายเซ็นค่าเริ่มต้นของระบบและเอกสารฉบับอื่น
              </div>

              <div class="row">
                <div class="col-lg-7">
                  <!--
                    design ข้อ 4 — คำกำกับบน/ล่างกรอบ ให้ผู้ใช้เห็นว่าภาพนี้จะไปอยู่ตรงไหนของฟอร์ม PDF
                    design ข้อ 5 — บอกตรงหัวกรอบว่าคลิกภาพแล้วขยายได้
                  -->
                  <div class="fss-sig__frame-caption">
                    กรอบลายเซ็นในฟอร์ม PDF
                    <span v-if="documentImageUrl" class="fss-sig__zoom-note">
                      (คลิกภาพเพื่อขยายดูใหญ่)
                    </span>
                  </div>

                  <!-- FS-113 — กรอบ preview จำลองช่อง "จัดทำโดย" ของฟอร์ม PDF ทั้งชุด -->
                  <div class="fss-sig__preview" data-test="fss-sig-preview-document">
                    <FormSummaryStockSignaturePdfPreview
                      :src="documentPreviewUrl"
                      alt="ลายเซ็นของเอกสารฉบับนี้"
                      empty-text="ยังไม่มีภาพลายเซ็น"
                      test-id="fss-pdfsig-document"
                      @load="onImageLoad('document', $event)"
                      @zoom="zoom(documentPreviewUrl)"
                    />
                  </div>

                  <!--
                    QA เรื่องที่ 14 — ภาพที่เพิ่งเลือกยัง "ไม่ถูกบันทึก"
                    ต้องบอกให้ชัดว่ายังต้องกดปุ่มด้านล่างก่อน ไม่งั้นผู้ใช้เข้าใจว่าเสร็จแล้ว
                  -->
                  <div
                    v-if="hasStagedDocument"
                    class="alert alert-warning fss-sig__staged"
                    data-test="fss-sig-staged-document"
                  >
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>
                      นี่คือภาพใหม่ที่เลือกไว้ — <strong>ยังไม่ได้บันทึก</strong>
                      กดปุ่ม "ใช้กับเอกสารนี้เท่านั้น" ด้านล่างเพื่อยืนยัน
                    </span>
                  </div>

                  <!-- FS-113 — ตัดคำกำกับ "ผู้จัดทำ" ใต้กรอบออก (ผู้ใช้สั่ง 16/10/2026 · ในกรอบมี "( จัดทำโดย )" แล้ว) -->
                </div>

                <div class="col-lg-5">
                  <!--
                    design ข้อ 6 — กล่อง "ไฟล์ปัจจุบัน"
                    ขนาดไฟล์/ขนาดภาพ: endpoint Signature คืนมาแค่ URL กับชื่อไฟล์
                    จึงอ่านจากฝั่ง client เท่าที่รู้จริง — ขนาดภาพจาก naturalWidth/Height ตอน @load
                    ส่วนขนาดไฟล์รู้เฉพาะไฟล์ที่ผู้ใช้เพิ่งเลือกในรอบนี้ (File.size)
                    ถ้าไม่รู้ให้ซ่อนบรรทัดนั้นไปเลย ห้ามแสดงค่าหลอก
                  -->
                  <div class="fss-sig__filecard">
                    <div class="fss-sig__filecard-title">
                      {{ hasStagedDocument ? "ไฟล์ที่เลือกใหม่" : "ไฟล์ปัจจุบัน" }}
                    </div>

                    <div
                      v-if="hasStagedDocument"
                      class="fss-sig__meta fss-sig__filename"
                    >
                      <i class="fas fa-file-image"></i> {{ stagedFileName }}
                    </div>
                    <div v-else-if="documentFileName" class="fss-sig__meta fss-sig__filename">
                      <i class="fas fa-file-image"></i> {{ documentFileName }}
                    </div>
                    <div v-else class="fss-sig__meta text-muted">
                      ยังไม่มีไฟล์ลายเซ็น
                    </div>

                    <div v-if="hasStagedDocument" class="fss-sig__meta text-muted">
                      ขนาดไฟล์ {{ stagedSizeText }}
                    </div>
                    <div v-else-if="documentSizeText" class="fss-sig__meta text-muted">
                      ขนาดไฟล์ {{ documentSizeText }}
                    </div>
                    <div v-if="documentDimensionText" class="fss-sig__meta text-muted">
                      ขนาดภาพ {{ documentDimensionText }}
                    </div>

                    <!-- FS-085 (14/10/2026) — ตัดป้าย "📌 แหล่งที่มา: …" ออกตามที่ QA แจ้ง
                         (เดิมมาจาก design ข้อ 7 / mockup ใน spec — บันทึกไว้ใน CR-Features หัวข้อ 6) -->

                    <!--
                      QA 08/10/2026 เรื่องที่ 20 — เดิมเป็นปุ่มแดงเต็มใบพร้อมข้อความยาว
                      เปลี่ยนเป็นวงกลมแดงตาม Design System (เรื่องที่ 16)
                      ข้อความเดิมย้ายไปอยู่ใน tooltip
                      เอกสารที่บันทึกแล้วต้องไม่มีปุ่มนี้ (ลายเซ็นถูกตรึง)

                      ── 12/10/2026 : ทำไมต้องรวม hasStagedDocument เข้ามา ──────────────
                      เรื่องที่ 20 เขียนว่าปุ่มนี้ "ปรากฏหลังอัพโหลดภาพแล้วเท่านั้น"
                      แต่เรื่องที่ 14 สั่งตัดการอัพโหลดทันทีออก จังหวะนั้นจึงไม่มีอยู่อีกแล้ว
                      ถ้ายึดตามตัวอักษร ปุ่มนี้จะไม่มีวันโผล่ = เรื่องที่ 20 ส่งมอบไม่ได้
                      จึงให้โผล่ตั้งแต่ตอน "พักไฟล์ไว้" ซึ่งเป็นจังหวะเดียวกับที่ลูกค้าเห็นปุ่มนี้
                      แล้วขอให้เปลี่ยนสี · ลิงก์ "ยกเลิกภาพที่เลือก" ถูกตัดออกไปแล้ว
                      เพื่อไม่ให้มีปุ่มทำงานเดียวกันซ้อนกัน 2 ตัว
                      ขนาดใช้ md (36px) ตามที่ผู้ใช้สั่ง — เป็นขนาดที่ mockup นิยามไว้เองอยู่แล้ว
                    -->
                    <div
                      v-if="canManage && !isDocumentLocked && (hasDocumentOverride || hasStagedDocument)"
                      class="fss-sig__delete-row"
                    >
                      <button
                        type="button"
                        class="fss-act fss-act--md fss-act--red"
                        data-test="fss-sig-delete-document"
                        :title="hasStagedDocument
                          ? STAGED_DISCARD_TITLE
                          : 'ลบภาพ (ถ้าลบจะกลับไปใช้ค่าเริ่มต้นระบบ)'"
                        :disabled="uploading"
                        @click="removeOrDiscardDocument"
                      >
                        <i class="la la-trash"></i>
                      </button>
                      <span class="fss-sig__delete-label text-muted">
                        {{ hasStagedDocument ? "ยกเลิกภาพที่เลือก" : "ลบภาพเฉพาะเอกสารนี้" }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!--
                ลายเซ็นถูกตรึง ณ วันบันทึก — เอกสารที่บันทึกแล้วเปลี่ยนไม่ได้
                (ฝั่ง server ปฏิเสธด้วย ไม่ได้กันแค่ที่หน้าจอ)
                สถานะนี้จึงไม่มีทั้ง dropzone และปุ่มลบ
              -->
              <div
                v-if="isDocumentLocked"
                class="fss-sig__readonly text-muted"
                data-test="fss-sig-locked"
              >
                <i class="fas fa-lock"></i>
                ลายเซ็นของเอกสารฉบับนี้ถูกตรึงไว้ตั้งแต่วันที่บันทึก เปลี่ยนไม่ได้อีก
                หากต้องการใช้ลายเซ็นอื่น ต้องสร้างเอกสารใหม่
              </div>

              <!-- design ข้อ 9 — ลากไฟล์มาวางได้ (แพตเทิร์นเดียวกับ popup แนบไฟล์เอกสาร) -->
              <div
                v-if="canManage && !isDocumentLocked"
                class="fss-sig__dropzone"
                :class="{
                  'fss-sig__dropzone--over': dragScope === 'document',
                  'fss-sig__dropzone--disabled': uploading,
                }"
                @click="pickFile('document')"
                @dragover.prevent="onDragOver('document')"
                @dragenter.prevent="onDragOver('document')"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop('document', $event)"
              >
                <i class="fas fa-cloud-upload-alt fss-sig__dropzone-icon"></i>
                <div class="fss-sig__dropzone-title">
                  ลากไฟล์ภาพลายเซ็นมาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์
                </div>
                <button
                  type="button"
                  class="btn btn-brand btn-sm fss-sig__dropzone-button"
                  data-test="fss-sig-upload-document"
                  :disabled="uploading"
                  @click.stop="pickFile('document')"
                >
                  <i class="fas fa-upload"></i> เลือกภาพลายเซ็น
                </button>
              </div>

              <!-- design ข้อ 10 — บรรทัดเงื่อนไขไฟล์อยู่ใต้ dropzone -->
              <div
                v-if="canManage && !isDocumentLocked"
                class="fss-sig__rules text-muted"
              >
                รองรับไฟล์ PNG และ JPG ขนาดไม่เกิน 10 MB —
                แนะนำ PNG พื้นหลังโปร่งใส ขนาดประมาณ 800 x 400 px
              </div>
            </div>
            <!-- end: แถบที่ 1 -->

            <!-- begin: แถบที่ 2 — ค่าเริ่มต้นระบบ -->
            <div v-show="activeTab === 'default'" data-test="fss-sig-pane-default">
              <!--
                แถบนี้ใช้กล่องเตือนสีแดงแทนสีฟ้า เพราะผลกระทบต่างระดับกับแถบที่ 1
                (spec: ไม่มีปุ่มลบ เปลี่ยนได้ด้วยการอัพโหลดทับเท่านั้น)
              -->
              <div class="alert alert-danger fss-sig__notice">
                <i class="fas fa-exclamation-circle"></i>
                ภาพนี้ใช้กับ<strong>ทุกเอกสาร</strong>ที่บันทึกหลังจากนี้ทั้งระบบ
                เอกสารที่บันทึกไปแล้วจะไม่เปลี่ยนตาม
                และแถบนี้<strong>ไม่มีปุ่มถอดภาพ</strong> เปลี่ยนได้ด้วยการอัพโหลดทับเท่านั้น
              </div>

              <div class="row">
                <div class="col-lg-7">
                  <div class="fss-sig__frame-caption">
                    กรอบลายเซ็นในฟอร์ม PDF
                    <span v-if="defaultImageUrl" class="fss-sig__zoom-note">
                      (คลิกภาพเพื่อขยายดูใหญ่)
                    </span>
                  </div>

                  <!-- FS-113 — กรอบ preview จำลองช่อง "จัดทำโดย" ของฟอร์ม PDF ทั้งชุด -->
                  <div class="fss-sig__preview" data-test="fss-sig-preview-default">
                    <FormSummaryStockSignaturePdfPreview
                      :src="defaultPreviewUrl"
                      alt="ลายเซ็นค่าเริ่มต้นของระบบ"
                      empty-text="ระบบยังไม่มีลายเซ็นค่าเริ่มต้น"
                      test-id="fss-pdfsig-default"
                      @load="onImageLoad('default', $event)"
                      @zoom="zoom(defaultPreviewUrl)"
                    />
                  </div>

                  <!--
                    QA เรื่องที่ 12 — spec บอกว่าต้องมีขั้น preview ก่อน
                    แล้วค่อยกด "บันทึกเป็นค่าเริ่มต้นระบบ →" จึงจะขึ้น popup ยืนยัน
                  -->
                  <div
                    v-if="hasStagedDefault"
                    class="alert alert-warning fss-sig__staged"
                    data-test="fss-sig-staged-default"
                  >
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>
                      นี่คือภาพใหม่ที่เลือกไว้ — <strong>ยังไม่ได้บันทึก</strong>
                      ตรวจดูให้เรียบร้อยแล้วกด "บันทึกเป็นค่าเริ่มต้นระบบ" ด้านล่าง
                    </span>
                    <!-- FS-086 — ลิงก์ "ยกเลิกภาพที่เลือก" ย้ายไปเป็นปุ่มวงกลมแดงใต้การ์ดขวา (แบบเดียวกับแถบ 1) -->
                  </div>

                  <!-- FS-113 — ตัดคำกำกับ "ผู้จัดทำ" ใต้กรอบออก (ผู้ใช้สั่ง 16/10/2026 · ในกรอบมี "( จัดทำโดย )" แล้ว) -->
                </div>

                <div class="col-lg-5">
                  <div class="fss-sig__filecard">
                    <div class="fss-sig__filecard-title">
                      {{ hasStagedDefault ? "ไฟล์ที่เลือกใหม่" : "ไฟล์ปัจจุบัน" }}
                    </div>

                    <div v-if="hasStagedDefault" class="fss-sig__meta fss-sig__filename">
                      <i class="fas fa-file-image"></i> {{ stagedFileName }}
                    </div>
                    <div v-else-if="defaultFileName" class="fss-sig__meta fss-sig__filename">
                      <i class="fas fa-file-image"></i> {{ defaultFileName }}
                    </div>
                    <div v-else class="fss-sig__meta text-muted">
                      ยังไม่มีไฟล์ลายเซ็น
                    </div>

                    <div v-if="hasStagedDefault" class="fss-sig__meta text-muted">
                      ขนาดไฟล์ {{ stagedSizeText }}
                    </div>
                    <div v-else-if="defaultSizeText" class="fss-sig__meta text-muted">
                      ขนาดไฟล์ {{ defaultSizeText }}
                    </div>
                    <div v-if="defaultDimensionText" class="fss-sig__meta text-muted">
                      ขนาดภาพ {{ defaultDimensionText }}
                    </div>
                    <div v-if="defaultUpdatedAtText" class="fss-sig__meta text-muted">
                      แก้ไขล่าสุด {{ defaultUpdatedAtText }}
                    </div>

                    <!-- FS-085 — ตัดป้าย "📌 แหล่งที่มา: ค่าเริ่มต้นระบบ" ออก (เหมือนแถบ 1) -->

                    <!--
                      FS-086 (14/10/2026) — ปุ่มวงกลมแดงแบบแถบ 1 แทนลิงก์ในกล่องเหลือง
                      ⚠ spec: แถบนี้ "ไม่มีปุ่มลบ — ระบบต้องมีภาพลายเซ็นค่าเริ่มต้นเสมอ"
                        ปุ่มนี้จึงโผล่เฉพาะตอนมีภาพที่ "เลือกไว้แต่ยังไม่บันทึก" และทำได้แค่ทิ้งภาพนั้น
                        ไม่แตะภาพค่าเริ่มต้นที่บันทึกแล้ว (เรียก discardStagedFile ตัวเดิมของลิงก์)
                    -->
                    <div v-if="canManage && hasStagedDefault" class="fss-sig__delete-row">
                      <button
                        type="button"
                        class="fss-act fss-act--md fss-act--red"
                        data-test="fss-sig-discard-default"
                        :title="STAGED_DISCARD_TITLE"
                        :disabled="uploading"
                        @click="discardStagedFile"
                      >
                        <i class="la la-trash"></i>
                      </button>
                      <span class="fss-sig__delete-label text-muted">ยกเลิกภาพที่เลือก</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="canManage"
                class="fss-sig__dropzone fss-sig__dropzone--danger"
                :class="{
                  'fss-sig__dropzone--over': dragScope === 'default',
                  'fss-sig__dropzone--disabled': uploading,
                }"
                @click="pickFile('default')"
                @dragover.prevent="onDragOver('default')"
                @dragenter.prevent="onDragOver('default')"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop('default', $event)"
              >
                <i class="fas fa-cloud-upload-alt fss-sig__dropzone-icon"></i>
                <div class="fss-sig__dropzone-title">
                  ลากไฟล์ภาพลายเซ็นมาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์
                </div>
                <button
                  type="button"
                  class="btn btn-danger btn-sm fss-sig__dropzone-button"
                  data-test="fss-sig-upload-default"
                  :disabled="uploading"
                  @click.stop="pickFile('default')"
                >
                  <i class="fas fa-upload"></i> อัพโหลดทับค่าเริ่มต้นระบบ
                </button>
              </div>

              <div v-if="canManage" class="fss-sig__rules text-muted">
                รองรับไฟล์ PNG และ JPG ขนาดไม่เกิน 10 MB —
                แนะนำ PNG พื้นหลังโปร่งใส ขนาดประมาณ 800 x 400 px
              </div>
            </div>
            <!-- end: แถบที่ 2 -->
          </div>

          <!--
            design ข้อ 11 — แถบเตือนสีเหลืองท้ายเนื้อหา (อยู่นอกแถบ เพราะใช้กับทั้ง 2 แถบ)
            ย้ำ 2 กติกาที่ผู้ใช้เข้าใจผิดบ่อย: ล็อก ณ วันบันทึก + ชุดเอกสารรวมใช้ภาพเดียวกัน
          -->
          <div class="alert alert-warning fss-sig__lock-note" role="alert">
            <i class="fas fa-exclamation-triangle"></i>
            <strong>ข้อสำคัญ</strong> — ลายเซ็นจะถูกตรึงไว้กับเอกสาร ณ วันที่กดบันทึก
            เอกสารที่บันทึกไปแล้วจะไม่เปลี่ยนตามแม้ภายหลังจะเปลี่ยนค่าเริ่มต้นของระบบ
            และเอกสารทุกฉบับที่สร้างพร้อมกันในชุดเดียวจะใช้ภาพลายเซ็นเดียวกันทั้งหมด
          </div>

          <!-- ผู้ใช้ที่ไม่มีสิทธิ์เห็นได้อย่างเดียว ตาม spec -->
          <div v-if="!canManage" class="fss-sig__readonly text-muted">
            <i class="fas fa-lock"></i>
            ดูได้อย่างเดียว — การจัดการลายเซ็นผู้จัดทำสงวนไว้สำหรับแอดมินทีมเบียร์สด
          </div>

          <!--
            input file ตัวเดียวใช้ร่วมกันทั้ง 2 แถบ (แยกด้วย pendingScope)
            ห้ามเพิ่มตัวที่สอง — เทส UI-15 เลือกไฟล์ด้วย
            '#formSummaryStockSignatureModal input[type="file"]' ซึ่งต้องชี้ได้ตัวเดียว
          -->
          <input
            type="file"
            ref="fileInput"
            class="fss-sig__file"
            accept=".png,.jpg,.jpeg"
            @change="onFileChosen"
          />

          <!--
            design ข้อ 5 — ขยายภาพด้วยชั้นซ้อนใน popup เอง
            (ไม่เพิ่ม library ใหม่ · คลิกที่ใดก็ได้เพื่อปิด)
          -->
          <div v-if="zoomImageUrl" class="fss-sig__zoom" @click="closeZoom">
            <img :src="zoomImageUrl" class="fss-sig__zoom-image" alt="ภาพลายเซ็นขยาย" />
            <div class="fss-sig__zoom-close">คลิกที่ใดก็ได้เพื่อปิด</div>
          </div>
        </div>

        <!--
          design ข้อ 12 — ปุ่มท้าย popup
          QA 08/10/2026 เรื่องที่ 12/14 — ตอนนี้เป็น 2 ขั้นจริงแล้ว
            เลือกไฟล์ = preview เท่านั้น → กดปุ่มของแถบนั้นถึงจะอัพโหลด
          ปุ่มหลักจึงเปลี่ยนตามแถบที่เปิดอยู่
            แถบ 1 → "ใช้กับเอกสารนี้เท่านั้น"      (สีหลักของระบบ ตามเรื่องที่ 19)
            แถบ 2 → "บันทึกเป็นค่าเริ่มต้นระบบ →"  (สีส้มตาม spec เพราะกระทบทั้งระบบ)
          ส่วน "ยกเลิก" = ปิดเฉย ๆ โดยไม่เปลี่ยน selection ที่หน้าแม่ถืออยู่
          ⚠ ปุ่มที่มี data-dismiss ในแถบนี้ต้องมีตัวเดียว — เทส UI-15 อ้างอิงด้วย selector นั้น
        -->
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-dismiss="modal">
            {{ isDocumentLocked ? "ปิด" : "ยกเลิก" }}
          </button>

          <!-- เรื่องที่ 19 — เดิมเป็น btn-warning (เหลือง) ขัดกับปุ่ม "บันทึกข้อมูล" ด้านนอก -->
          <button
            v-if="showApplyButton"
            type="button"
            class="btn btn-primary"
            data-test="fss-sig-apply-document"
            :disabled="uploading"
            @click="applySelection"
          >
            <i class="fas fa-check"></i> ใช้กับเอกสารนี้เท่านั้น
          </button>

          <!-- เรื่องที่ 12 — ปุ่มที่ spec กำหนดไว้แต่เดิมไม่มีในระบบ -->
          <button
            v-if="showSaveDefaultButton"
            type="button"
            class="btn fss-sig__save-default"
            data-test="fss-sig-save-default"
            :disabled="uploading || !hasStagedDefault"
            @click="saveAsSystemDefault"
          >
            บันทึกเป็นค่าเริ่มต้นระบบ <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

// Action Button Design System ของโมดูลนี้ (QA เรื่องที่ 16) — ใช้ที่ปุ่มลบภาพ
import "./form-summary-stock-actions.css";
// FS-088 — tooltip ของปุ่มวงกลมเป็นแบบ Bootstrap (ผูกครั้งเดียวทั้งโมดูล เรียกซ้ำได้)
import { installFssTooltips } from "./form-summary-stock-tooltips";
installFssTooltips();

require("sweetalert2");

// ─────────────────────────────────────────────────────────────────────────────
// CR-FormSummaryStock F15 — popup "จัดการลายเซ็นผู้จัดทำ" 2 แถบ
//
// เปิดจากหน้าตรวจสอบ/รายละเอียดเอกสารผ่าน $refs:
//   this.$refs.signatureModal.open({ id, documentNo })
//   เอกสารที่ยังไม่ถูกบันทึกให้ส่ง id = 0 — component จะคืน attachedId ผ่าน event
//   "selected" ให้หน้าแม่ถือไว้ แล้วแนบไปกับคำสั่งบันทึก (query string signatureAttachedId)
//
// spec ฉบับเซ็น 16/09/2026:
//   • แถบ 1 "ใช้กับเอกสารนี้เท่านั้น"        — ไม่กระทบค่าเริ่มต้น / มีปุ่มลบ
//   • แถบ 2 "จัดการลายเซ็นค่าเริ่มต้นระบบ"  — กระทบทั้งระบบ / ไม่มีปุ่มลบ / ยืนยันซ้ำก่อนบันทึก
//   • เห็นปุ่มจัดการเฉพาะแอดมินทีมเบียร์สด (canManage มาจาก API ไม่ใช่เดาจาก role ฝั่ง client)
//
// FS-013 (24/09/2026) — จัดหน้าใหม่ให้ตรง design: หัวสีน้ำเงิน · ป้าย "กระทบทั้งระบบ" ·
//   กล่องคำอธิบายสีฟ้า · คำกำกับกรอบลายเซ็น · คลิกภาพเพื่อขยาย · กล่องข้อมูลไฟล์ ·
//   ป้ายแหล่งที่มา · ปุ่มลบเต็มใบ · dropzone · แถบเตือนสีเหลือง · ปุ่มยืนยันท้าย popup
//
// SweetAlert2 = 8.13.3 (SBP.Web/package.json ^8.2.3) → ใช้ option `type` (ไม่ใช่ `icon` ของ v9+)
// และอ่านผลการยืนยันจาก `result.value`
//
// กฎชนิด/ขนาดไฟล์ถูกบังคับซ้ำที่ server ใน UploadFormSummaryStockSignatureCommandHandler
// ที่ตรวจในนี้เป็นแค่ด่านแรกเพื่อ UX
// ─────────────────────────────────────────────────────────────────────────────

// ตรงกับ FormSummaryStockSignatureRules.AllowedExtensions (backend)
const ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg"];

// 10 MB — ตรงกับ FormSummaryStockSignatureRules.MaxFileSizeBytes
const MAX_FILE_SIZE = 10 * 1024 * 1024;

function getExtension(fileName) {
  if (!fileName) return "";
  const dotIndex = String(fileName).lastIndexOf(".");
  if (dotIndex < 0) return "";
  return String(fileName)
    .substring(dotIndex)
    .toLowerCase();
}

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return "-";
  if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  if (bytes >= 1024) return (bytes / 1024).toFixed(2) + " KB";
  return bytes + " B";
}

import FormSummaryStockSignaturePdfPreview from "./FormSummaryStockSignaturePdfPreview";

export default {
  components: { FormSummaryStockSignaturePdfPreview },
  data: function() {
    return {
      // FS-087 — tooltip ปุ่มแดงตอนภาพ "เลือกไว้แต่ยังไม่บันทึก" ใช้ข้อความเดียวกันทั้งสองแถบ
      //   ข้อความตามที่ผู้ใช้กำหนด 14/10/2026 — "ยกเลิกภาพที่เลือก" ติดวงเล็บ ไม่เว้นวรรค
      STAGED_DISCARD_TITLE: "ยกเลิกภาพที่เลือก(ยังไม่บันทึกจะกลับไปใช้ค่าเริ่มต้นระบบ)",

      // 0 = เอกสารยังไม่ถูกบันทึก (เปิดจากหน้าตรวจสอบก่อนกดบันทึก)
      headerId: 0,
      documentNo: "",
      activeTab: "document",

      canManage: false,
      isMissingDefault: false,
      hasDocumentOverride: false,

      defaultAttachedId: null,
      defaultImageUrl: "",
      defaultFileName: "",
      defaultUpdatedAt: null,

      documentAttachedId: null,
      documentImageUrl: "",
      documentFileName: "",

      // ── design ข้อ 6 : ขนาดไฟล์ + ขนาดภาพ ──────────────────────────────
      // endpoint Signature ไม่ได้คืนค่าพวกนี้ (และห้ามแก้ฝั่ง backend ในรอบนี้)
      // จึงรู้ "ขนาดไฟล์" เฉพาะไฟล์ที่ผู้ใช้เพิ่งเลือกในรอบนี้ (File.size)
      // ส่วน "ขนาดภาพ" อ่านจากภาพที่โหลดเสร็จ (naturalWidth/naturalHeight)
      // 0 = ไม่รู้ → ซ่อนบรรทัดนั้นไปเลย ไม่แสดงค่าหลอก
      documentFileSize: 0,
      documentImageWidth: 0,
      documentImageHeight: 0,
      defaultFileSize: 0,
      defaultImageWidth: 0,
      defaultImageHeight: 0,

      // โหมดก่อนบันทึก (headerId = 0) — เอกสารยังไม่มีแถวใน DB ให้ผูก
      // API จึงคืน documentAttachedId = null เสมอ ต้องจำภาพที่เพิ่งอัพโหลดไว้เองเพื่อโชว์ preview
      pendingAttachedId: null,
      pendingFileName: "",
      pendingFileSize: 0,
      pendingObjectUrl: "",

      // ── ภาพที่ผู้ใช้เลือกไว้แต่ "ยังไม่บันทึก" (QA 08/10/2026 เรื่องที่ 12/13/14) ──
      //
      // เดิมเลือกไฟล์ปุ๊บ = อัพโหลดทันที ทำให้
      //   • แถบ 1 ขึ้น popup "สำเร็จ" ทั้งที่ยังไม่ได้กดปุ่ม (เรื่องที่ 14)
      //   • thumbnail ปุ่มล่างขวาเปลี่ยนก่อนบันทึก และกดยกเลิกแล้วไม่คืนค่า (เรื่องที่ 13)
      //   • แถบ 2 ขึ้น popup ยืนยันทันที ไม่มีขั้น preview (เรื่องที่ 12)
      //
      // ตอนนี้เลือกไฟล์ = พักไว้ดูก่อนเท่านั้น ยังไม่ยิง API และยังไม่ emit อะไรให้หน้าแม่
      // จะอัพโหลดจริงเมื่อกดปุ่มบันทึกของแถบนั้นเท่านั้น
      stagedFile: null,
      stagedScope: "", // "document" | "default" | "" (ไม่มี)
      stagedPreviewUrl: "",
      stagedFileName: "",
      stagedFileSize: 0,

      // แถบที่กดปุ่มอัพโหลดไว้ — ใช้ตอน input file ยิง change กลับมา
      pendingScope: "document",
      // แถบที่กำลังถูกลากไฟล์มาวาง ("" = ไม่มี) ใช้ไฮไลต์เฉพาะ dropzone ที่ชี้อยู่
      dragScope: "",
      zoomImageUrl: "",
      loading: false,
      uploading: false,
    };
  },
  computed: {
    /**
     * เอกสารที่บันทึกแล้ว (มี headerId) จะเปลี่ยนลายเซ็นไม่ได้
     * ยังไม่บันทึก (headerId = 0) คือหน้าขั้นที่ 3 / 3B ซึ่งยังเลือกได้อยู่
     *
     * ⚠ เดิม computed ถูกประกาศ 2 ก้อนในไฟล์เดียว ก้อนหลังทับก้อนแรก
     *   ทำให้ isDocumentLocked หายไปทั้งตัว (ป้าย fss-sig-locked จึงไม่เคยขึ้น)
     *   FS-013 รวมเป็นก้อนเดียวแล้ว
     */
    isDocumentLocked: function() {
      return this.headerId > 0;
    },

    /**
     * ปุ่มหลักท้าย popup แสดงเฉพาะตอนที่ "เลือกแล้วมีผล" ได้จริง
     * — เอกสารบันทึกแล้ว (ล็อก) หรือคนที่ไม่มีสิทธิ์จัดการ จะเห็นแค่ปุ่มปิด
     */
    showApplyButton: function() {
      return this.canManage && !this.isDocumentLocked && this.activeTab === "document";
    },

    // ── ภาพที่พักไว้ยังไม่บันทึก (เรื่องที่ 12/13/14) ────────────────────
    hasStagedDocument: function() {
      return this.stagedScope === "document" && !!this.stagedPreviewUrl;
    },
    hasStagedDefault: function() {
      return this.stagedScope === "default" && !!this.stagedPreviewUrl;
    },

    /**
     * ภาพที่แสดงในกรอบ preview ของแต่ละแถบ
     * ถ้ามีภาพที่เพิ่งเลือก (ยังไม่บันทึก) ให้โชว์ภาพนั้นเพื่อให้ตรวจก่อน
     * ถ้าไม่มี ก็โชว์ภาพที่บันทึกไว้จริงตามเดิม
     */
    documentPreviewUrl: function() {
      return this.hasStagedDocument ? this.stagedPreviewUrl : this.documentImageUrl;
    },
    defaultPreviewUrl: function() {
      return this.hasStagedDefault ? this.stagedPreviewUrl : this.defaultImageUrl;
    },

    /**
     * ปุ่ม "บันทึกเป็นค่าเริ่มต้นระบบ →" ของแถบ 2 (spec บรรทัด 2857-2860)
     * โผล่เฉพาะตอนมีภาพใหม่รออยู่ — ไม่มีภาพใหม่ก็ไม่มีอะไรให้บันทึก
     */
    showSaveDefaultButton: function() {
      return this.canManage && this.activeTab === "default";
    },

    defaultUpdatedAtText: function() {
      if (!this.defaultUpdatedAt) return "";
      return moment(this.defaultUpdatedAt).format("DD/MM/YYYY HH:mm");
    },

    documentSizeText: function() {
      return this.documentFileSize > 0 ? formatBytes(this.documentFileSize) : "";
    },
    documentDimensionText: function() {
      if (!this.documentImageWidth || !this.documentImageHeight) return "";
      return this.documentImageWidth + " x " + this.documentImageHeight + " px";
    },
    defaultSizeText: function() {
      return this.defaultFileSize > 0 ? formatBytes(this.defaultFileSize) : "";
    },
    defaultDimensionText: function() {
      if (!this.defaultImageWidth || !this.defaultImageHeight) return "";
      return this.defaultImageWidth + " x " + this.defaultImageHeight + " px";
    },
    stagedSizeText: function() {
      return this.stagedFileSize > 0 ? formatBytes(this.stagedFileSize) : "";
    },
  },
  methods: {
    // ── เปิด/ปิด popup ────────────────────────────────────────────────────
    open: function(row) {
      this.headerId = row && row.id ? row.id : 0;
      this.documentNo = (row && row.documentNo) || "";
      this.activeTab = "document";
      this.pendingScope = "document";
      this.dragScope = "";
      this.zoomImageUrl = "";
      this.uploading = false;

      // ข้อมูลที่รู้ได้เฉพาะฝั่ง client ต้องล้างทุกครั้งที่เปิดใหม่
      // ไม่งั้นจะเอาขนาดไฟล์/ภาพของเอกสารใบก่อนมาโชว์
      //
      // FS-070 — ยกเว้นภาพที่บันทึกไว้แล้วในโหมดก่อนบันทึก ซึ่งหน้าแม่ยังถืออยู่
      //   โหมดนี้ (headerId = 0) server ยังไม่มีเอกสารให้ผูกภาพ ภาพนั้นจึงมีอยู่ที่นี่ที่เดียว
      //   ของเดิมล้างทิ้งทุกครั้ง → เปิดครั้งที่ 2 กรอบโชว์ภาพเก่า · ป้ายขึ้น "ค่าเริ่มต้นระบบ"
      //   · ปุ่มลบ (FS-064) ไม่โผล่ ทั้งที่เอกสารยังผูกกับภาพนั้นอยู่
      //   เก็บไว้เฉพาะเมื่อ attachedId ตรงกับที่หน้าแม่ส่งมา — ถ้าหน้าแม่ล้างภาพไปแล้ว ก็ล้างตาม
      const keepPending =
        !this.headerId &&
        !!(row && row.attachedId) &&
        row.attachedId === this.pendingAttachedId;
      this.clearClientOnlyInfo(keepPending);
      this.clearStagedFile();
      this.resetFileInput();

      if (this.$refs.modalRoot) {
        $(this.$refs.modalRoot).modal("show");
      }

      this.loadState();
    },

    close: function() {
      if (this.$refs.modalRoot) {
        $(this.$refs.modalRoot).modal("hide");
      }
    },

    /**
     * ปุ่มหลักท้าย popup — ยืนยันว่าจะใช้ภาพที่เห็นอยู่กับเอกสารฉบับนี้
     * ภาพถูกอัพโหลดไปแล้วตั้งแต่ตอนเลือกไฟล์ (flow เดิม ไม่ได้เปลี่ยนเป็น 2 ขั้น)
     * ปุ่มนี้จึงแค่ส่งค่าให้หน้าแม่ซ้ำอีกครั้งเพื่อความแน่นอน แล้วปิด popup
     */
    applySelection: function() {
      // QA เรื่องที่ 14 — มีภาพใหม่รออยู่ ให้อัพโหลดตอนนี้ (ตอนเลือกไฟล์ยังไม่ได้อัพ)
      // upload() จะ emit ให้หน้าแม่และปิด popup ให้เองเมื่อสำเร็จ
      if (this.hasStagedDocument && this.stagedFile) {
        this.upload(this.stagedFile, 0, true);
        return;
      }

      if (!this.headerId) {
        // FS-017 — ส่ง url ของภาพกลับไปด้วย (อาร์กิวเมนต์ที่ 2) เพื่อให้หน้าแม่โชว์ thumbnail ได้
        // ผู้ฟังเดิมรับอาร์กิวเมนต์เดียว การเพิ่มท้ายจึงไม่กระทบใคร
        this.$emit(
          "selected",
          this.pendingAttachedId || null,
          this.documentImageUrl || this.defaultImageUrl || ""
        );
      }
      this.close();
    },

    // ── โหลดสถานะลายเซ็นทั้ง 2 แถบ ────────────────────────────────────────
    loadState: function() {
      const self = this;
      self.loading = true;

      axios
        .post("/Api/FormSummaryStock/Signature", { headerId: self.headerId })
        .then(function(response) {
          const data = (response && response.data) || {};

          if (!data.success) {
            Swal.fire(
              "เกิดข้อผิดพลาด",
              data.message || "ไม่สามารถอ่านข้อมูลลายเซ็นได้",
              "error"
            );
            return;
          }

          self.canManage = !!data.canManage;
          self.isMissingDefault = !!data.isMissingDefault;
          self.hasDocumentOverride = !!data.hasDocumentOverride;

          self.defaultAttachedId = data.defaultAttachedId || null;
          self.defaultImageUrl = data.defaultImageUrl || "";
          self.defaultFileName = data.defaultFileName || "";
          self.defaultUpdatedAt = data.defaultUpdatedAt || null;

          self.documentAttachedId = data.documentAttachedId || null;
          self.documentImageUrl = data.documentImageUrl || "";
          self.documentFileName = data.documentFileName || "";
          self.documentFileSize = 0;

          if (!self.headerId && self.pendingAttachedId) {
            // โหมดก่อนบันทึก — เพิ่งอัพโหลดไปในรอบนี้
            // API ยังไม่มีเอกสารให้ผูก จึงต้องโชว์ภาพที่ผู้ใช้เพิ่งเลือก ไม่ใช่ค่าเริ่มต้น
            self.documentAttachedId = self.pendingAttachedId;
            self.documentImageUrl = self.pendingObjectUrl || self.documentImageUrl;
            self.documentFileName = self.pendingFileName || self.documentFileName;
            self.documentFileSize = self.pendingFileSize || 0;
            self.hasDocumentOverride = true;
          } else if (!self.documentImageUrl && self.defaultImageUrl) {
            // เอกสารยังไม่เคยเลือกภาพเฉพาะกิจ → โชว์ค่าเริ่มต้นให้เห็นว่าจะได้ภาพไหน
            self.documentImageUrl = self.defaultImageUrl;
            self.documentFileName = self.defaultFileName;
            self.documentFileSize = self.defaultFileSize;
          }
        })
        .catch(function() {
          Swal.fire(
            "เกิดข้อผิดพลาด",
            "ไม่สามารถอ่านข้อมูลลายเซ็นได้ กรุณาลองใหม่อีกครั้ง",
            "error"
          );
        })
        .then(function() {
          self.loading = false;
        });
    },

    // ── เลือกไฟล์ ─────────────────────────────────────────────────────────
    pickFile: function(scope) {
      if (!this.canManage || this.uploading) return;
      if (scope === "document" && this.isDocumentLocked) return;
      this.pendingScope = scope;
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    onFileChosen: function(event) {
      const input = event && event.target;
      const file = input && input.files && input.files.length ? input.files[0] : null;
      if (!file) return;

      // ใช้ "แถบที่เปิดอยู่ตอนไฟล์มาถึง" เป็นตัวตัดสิน ไม่ใช่แถบตอนที่กดปุ่ม
      //   input file ตัวเดียวใช้ร่วมกันทั้ง 2 แถบ ถ้าผู้ใช้กดปุ่มอัพโหลดแล้วสลับแถบ
      //   ก่อนเลือกไฟล์ (หน้าต่างเลือกไฟล์ของ OS ไม่ได้ล็อกหน้าจอ) ภาพจะไปเข้าแถบผิด
      //   แถบ 2 กระทบทั้งระบบ จึงต้องไม่พลาดเด็ดขาด
      //   ส่วนการลากไฟล์มาวางระบุแถบมาเองอยู่แล้วที่ onDrop()
      this.handleFile(file, this.activeTab === "default" ? "default" : "document");
    },

    // ── ลากไฟล์มาวาง (design ข้อ 9) ───────────────────────────────────────
    onDragOver: function(scope) {
      if (!this.canManage || this.uploading) return;
      if (scope === "document" && this.isDocumentLocked) return;
      this.dragScope = scope;
    },

    onDragLeave: function() {
      this.dragScope = "";
    },

    onDrop: function(scope, event) {
      this.dragScope = "";

      if (!this.canManage || this.uploading) return;
      if (scope === "document" && this.isDocumentLocked) return;

      const transfer = event && event.dataTransfer;
      const file = transfer && transfer.files && transfer.files.length ? transfer.files[0] : null;
      if (!file) return;

      this.pendingScope = scope;
      this.handleFile(file, scope);
    },

    /**
     * ทางเข้าเดียวของทั้ง "เลือกไฟล์" และ "ลากมาวาง"
     *
     * QA 08/10/2026 เรื่องที่ 12/13/14 — ตรงนี้ "ไม่อัพโหลดแล้ว"
     * เลือกไฟล์ = พักไว้ให้ดู preview เท่านั้น ยังไม่ยิง API ยังไม่ emit ให้หน้าแม่
     * ผู้ใช้ต้องกดปุ่มบันทึกของแถบนั้นเองถึงจะมีผลจริง
     */
    handleFile: function(file, scope) {
      if (!this.validate(file)) {
        this.resetFileInput();
        return;
      }

      this.stageFile(file, scope);
      this.resetFileInput();
    },

    // เก็บไฟล์ที่เลือกไว้รอบันทึก + สร้าง object URL ไว้โชว์ preview
    stageFile: function(file, scope) {
      this.clearStagedFile();

      this.stagedFile = file;
      this.stagedScope = scope === "default" ? "default" : "document";
      this.stagedFileName = file.name || "";
      this.stagedFileSize = file.size || 0;

      if (window.URL && window.URL.createObjectURL) {
        this.stagedPreviewUrl = window.URL.createObjectURL(file);
      }
    },

    // ล้างภาพที่พักไว้ + คืน object URL (กันหน่วยความจำรั่วเมื่อเลือกหลายรอบ)
    clearStagedFile: function() {
      if (this.stagedPreviewUrl && window.URL && window.URL.revokeObjectURL) {
        window.URL.revokeObjectURL(this.stagedPreviewUrl);
      }
      this.stagedFile = null;
      this.stagedScope = "";
      this.stagedPreviewUrl = "";
      this.stagedFileName = "";
      this.stagedFileSize = 0;
    },

    // ปุ่ม "เลือกภาพใหม่" ข้างแถบแจ้งเตือน — ทิ้งภาพที่พักไว้ กลับไปใช้ภาพเดิม
    discardStagedFile: function() {
      this.clearStagedFile();
      this.resetFileInput();
    },

    validate: function(file) {
      const extension = getExtension(file.name);

      if (ALLOWED_EXTENSIONS.indexOf(extension) < 0) {
        Swal.fire(
          "ชนิดไฟล์ไม่รองรับ",
          "กรุณาใช้ไฟล์ PNG หรือ JPG เท่านั้น",
          "warning"
        );
        return false;
      }

      if (file.size > MAX_FILE_SIZE) {
        Swal.fire(
          "ไฟล์ใหญ่เกินกำหนด",
          "ไฟล์มีขนาด " + formatBytes(file.size) + " ซึ่งเกิน 10 MB กรุณาย่อขนาดภาพก่อน",
          "warning"
        );
        return false;
      }

      return true;
    },

    /**
     * ปุ่ม "บันทึกเป็นค่าเริ่มต้นระบบ →" ของแถบ 2 (QA เรื่องที่ 12)
     * spec บรรทัด 2873 — กดปุ่มนี้แล้วระบบต้องถามยืนยันซ้ำอีกครั้งก่อนบันทึกจริง
     */
    saveAsSystemDefault: function() {
      if (!this.canManage || this.uploading) return;

      if (!this.hasStagedDefault || !this.stagedFile) {
        Swal.fire(
          "ยังไม่ได้เลือกภาพ",
          "กรุณาเลือกภาพลายเซ็นใหม่ก่อน แล้วตรวจดูในกรอบด้านบนให้เรียบร้อย",
          "info"
        );
        return;
      }

      this.confirmThenUploadDefault(this.stagedFile);
    },

    // ── แถบ 2 ต้องยืนยันซ้ำก่อนบันทึก (spec) ──────────────────────────────
    confirmThenUploadDefault: function(file) {
      const self = this;

      Swal.fire({
        title: "ยืนยันเปลี่ยนลายเซ็นค่าเริ่มต้นของระบบ",
        html:
          "ภาพนี้จะถูกใช้กับ<strong>ทุกเอกสาร</strong>ที่บันทึกหลังจากนี้ทั้งระบบ<br>" +
          "และไม่สามารถลบได้ เปลี่ยนได้ด้วยการอัพโหลดทับเท่านั้น<br><br>" +
          "ต้องการดำเนินการต่อหรือไม่",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "ยืนยันเปลี่ยนค่าเริ่มต้น",
        cancelButtonText: "ยกเลิก",
        confirmButtonClass: "btn btn-danger",
        cancelButtonClass: "btn btn-secondary",
      }).then(function(result) {
        // SweetAlert2 v8 — ผลการยืนยันอยู่ที่ result.value
        if (result.value) {
          self.upload(file, 1);
        } else {
          self.resetFileInput();
        }
      });
    },

    /**
     * อัพโหลดจริง — เรียกจากปุ่มบันทึกของแต่ละแถบเท่านั้น ไม่ได้เรียกตอนเลือกไฟล์แล้ว
     *
     * @param scope       0 = เฉพาะเอกสารนี้ (แถบ 1) · 1 = ค่าเริ่มต้นระบบ (แถบ 2)
     * @param closeAfter  true = ปิด popup เมื่อสำเร็จ (ใช้กับปุ่มของแถบ 1)
     */
    upload: function(file, scope, closeAfter) {
      const self = this;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("scope", scope);
      formData.append("headerId", self.headerId || 0);

      self.uploading = true;
      KTApp.block("#formSummaryStockSignatureModal .modal-content", {});

      axios
        .post("/Api/FormSummaryStock/Signature/Upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(function(response) {
          const data = (response && response.data) || {};

          if (!data.success) {
            Swal.fire("ไม่สำเร็จ", data.message || "อัพโหลดไม่สำเร็จ", "error");
            return;
          }

          Swal.fire("สำเร็จ", data.message, "success");

          // ขนาดไฟล์รู้ได้เฉพาะจากไฟล์ที่ผู้ใช้เพิ่งเลือก — เก็บไว้โชว์ในกล่อง "ไฟล์ปัจจุบัน"
          if (scope === 1) {
            self.defaultFileSize = file.size;
          } else {
            self.documentFileSize = file.size;
          }

          // บันทึกแล้ว — ภาพไม่ได้ "รออยู่" อีกต่อไป ล้างสถานะพักไว้ทิ้ง
          self.clearStagedFile();

          // เอกสารที่ยังไม่ถูกบันทึก — ส่ง attachedId ให้หน้าแม่ถือไว้ใช้ตอนกดบันทึก
          //
          // QA เรื่องที่ 13 — จุดนี้คือจุดเดียวที่ thumbnail ของหน้าแม่จะเปลี่ยน
          // และมาถึงได้ก็ต่อเมื่อผู้ใช้กดปุ่มบันทึกแล้วเท่านั้น
          if (scope === 0 && !self.headerId) {
            self.rememberPendingImage(file, data.attachedId || null);
            // FS-017 — ภาพที่เพิ่งเลือกยังไม่มี signed URL จาก server จึงส่ง object URL ในเครื่องไปก่อน
            self.$emit(
              "selected",
              data.attachedId || null,
              self.pendingObjectUrl || self.documentImageUrl || ""
            );
          }

          self.loadState();
          self.$emit("changed");

          if (closeAfter) self.close();
        })
        .catch(function() {
          Swal.fire(
            "เกิดข้อผิดพลาด",
            "อัพโหลดภาพลายเซ็นไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
            "error"
          );
        })
        .then(function() {
          self.uploading = false;
          self.resetFileInput();
          KTApp.unblock("#formSummaryStockSignatureModal .modal-content");
        });
    },

    // ปุ่มแดงในแถบ 1 ทำสองหน้าที่ตามสถานะ — ภาพยังพักอยู่ = "ยกเลิกภาพที่เลือก"
    // ส่วนภาพที่บันทึกไว้แล้ว = "ลบภาพเฉพาะเอกสารนี้" ซึ่งต้องถามยืนยันก่อน
    removeOrDiscardDocument: function() {
      if (this.hasStagedDocument) {
        this.discardStagedFile();
        return;
      }
      this.removeDocumentSignature();
    },

    // ── ลบภาพเฉพาะกิจ (แถบ 1 เท่านั้น) ────────────────────────────────────
    removeDocumentSignature: function() {
      const self = this;

      if (!self.headerId) {
        // ยังไม่บันทึก — แค่ล้างค่าที่หน้าแม่ถือไว้ ไม่ต้องเรียก API
        self.clearPendingImage();
        self.documentAttachedId = null;
        self.hasDocumentOverride = false;
        self.documentImageUrl = self.defaultImageUrl;
        self.documentFileName = self.defaultFileName;
        self.documentFileSize = self.defaultFileSize;
        // FS-017 — ล้างภาพเฉพาะกิจแล้ว เอกสารจะกลับไปใช้ลายเซ็นค่าเริ่มต้นของระบบ
        self.$emit("selected", null, self.defaultImageUrl || "");
        return;
      }

      Swal.fire({
        title: "ยืนยันลบลายเซ็นเฉพาะเอกสารนี้",
        text: "เอกสารฉบับนี้จะกลับไปใช้ลายเซ็นค่าเริ่มต้นของระบบ",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "ยืนยันลบ",
        cancelButtonText: "ยกเลิก",
        confirmButtonClass: "btn btn-danger",
        cancelButtonClass: "btn btn-secondary",
      }).then(function(result) {
        if (!result.value) return;

        KTApp.block("#formSummaryStockSignatureModal .modal-content", {});

        axios
          .post("/Api/FormSummaryStock/Signature/Delete", { headerId: self.headerId })
          .then(function(response) {
            const data = (response && response.data) || {};

            if (!data.success) {
              Swal.fire("ไม่สำเร็จ", data.message || "ลบไม่สำเร็จ", "error");
              return;
            }

            Swal.fire("สำเร็จ", data.message, "success");
            self.loadState();
            self.$emit("changed");
          })
          .catch(function() {
            Swal.fire(
              "เกิดข้อผิดพลาด",
              "ลบลายเซ็นไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
              "error"
            );
          })
          .then(function() {
            KTApp.unblock("#formSummaryStockSignatureModal .modal-content");
          });
      });
    },

    // ── ข้อมูลที่อ่านได้เฉพาะฝั่ง client (design ข้อ 5 · 6) ────────────────
    onImageLoad: function(scope, event) {
      const image = event && event.target;
      if (!image) return;

      if (scope === "default") {
        this.defaultImageWidth = image.naturalWidth || 0;
        this.defaultImageHeight = image.naturalHeight || 0;
      } else {
        this.documentImageWidth = image.naturalWidth || 0;
        this.documentImageHeight = image.naturalHeight || 0;
      }
    },

    zoom: function(url) {
      if (url) this.zoomImageUrl = url;
    },

    closeZoom: function() {
      this.zoomImageUrl = "";
    },

    /**
     * โหมดก่อนบันทึก — จำภาพที่เพิ่งอัพโหลดไว้เอง
     * ใช้ object URL ของไฟล์ที่ผู้ใช้เลือก เพราะ API คืน documentImageUrl ให้ไม่ได้
     * (เอกสารยังไม่มีแถวใน DB ให้ผูก AttachedId)
     */
    rememberPendingImage: function(file, attachedId) {
      this.revokePendingObjectUrl();
      this.pendingAttachedId = attachedId;
      this.pendingFileName = file ? file.name : "";
      this.pendingFileSize = file ? file.size : 0;
      this.pendingObjectUrl =
        file && window.URL && window.URL.createObjectURL
          ? window.URL.createObjectURL(file)
          : "";
    },

    clearPendingImage: function() {
      this.revokePendingObjectUrl();
      this.pendingAttachedId = null;
      this.pendingFileName = "";
      this.pendingFileSize = 0;
    },

    // object URL กินหน่วยความจำจนกว่าจะ revoke — ปล่อยทุกครั้งที่เลิกใช้ภาพนั้น
    revokePendingObjectUrl: function() {
      if (this.pendingObjectUrl && window.URL && window.URL.revokeObjectURL) {
        window.URL.revokeObjectURL(this.pendingObjectUrl);
      }
      this.pendingObjectUrl = "";
    },

    clearClientOnlyInfo: function(keepPending) {
      // FS-070 — keepPending = true ห้ามล้างภาพที่บันทึกไว้ และห้าม revoke object URL
      //   เพราะ thumbnail บนปุ่มของหน้าแม่ใช้ URL เดียวกันนี้อยู่ revoke ไปภาพหน้าแม่จะแตก
      if (!keepPending) this.clearPendingImage();
      this.documentFileSize = 0;
      this.documentImageWidth = 0;
      this.documentImageHeight = 0;
      this.defaultFileSize = 0;
      this.defaultImageWidth = 0;
      this.defaultImageHeight = 0;
    },

    // input file เดิมถือไฟล์เก่าไว้ — ถ้าไม่ล้าง เลือกไฟล์ชื่อเดิมซ้ำจะไม่ยิง change
    resetFileInput: function() {
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
  },
  mounted: function() {
    const self = this;

    // QA เรื่องที่ 13 — ปิด popup ด้วยปุ่ม × หรือ "ยกเลิก" ต้องทิ้งภาพที่เลือกไว้
    // ปุ่มสองตัวนั้นใช้ data-dismiss ของ Bootstrap จึงไม่ผ่าน close() ของเรา
    // ต้องดักที่ event ของ modal เองถึงจะครอบทุกทางออก (รวมกด ESC และคลิกนอกกรอบ)
    if (this.$refs.modalRoot) {
      $(this.$refs.modalRoot).on("hidden.bs.modal", function() {
        self.clearStagedFile();
        self.resetFileInput();
      });
    }
  },
  beforeDestroy: function() {
    if (this.$refs.modalRoot) {
      $(this.$refs.modalRoot).off("hidden.bs.modal");
    }
    this.clearStagedFile();
    this.revokePendingObjectUrl();
  },
};
</script>

<style scoped>

/* ── QA 08/10/2026 เรื่องที่ 12/14 — แถบเตือน "ภาพนี้ยังไม่ถูกบันทึก" ───── */
.fss-sig__staged {
  margin-top: 10px;
  margin-bottom: 0;
  padding: 10px 12px;
  font-size: 12.5px;
  line-height: 1.6;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
}
.fss-sig__staged > i {
  flex: 0 0 auto;
  margin-top: 2px;
}
.fss-sig__staged > span {
  flex: 1 1 220px;
  min-width: 0;
}
/* ── เรื่องที่ 20 — ปุ่มลบภาพแบบวงกลม + คำอธิบายข้าง ๆ ──────────────────── */
.fss-sig__delete-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
.fss-sig__delete-label {
  font-size: 12px;
}

/* ── เรื่องที่ 12 — ปุ่ม "บันทึกเป็นค่าเริ่มต้นระบบ" สีส้มตาม spec ───────── */
.fss-sig__save-default {
  background: #E3552C;
  border-color: #E3552C;
  color: #fff;
}
.fss-sig__save-default:hover:not(:disabled),
.fss-sig__save-default:focus:not(:disabled) {
  background: #c8461f;
  border-color: #c8461f;
  color: #fff;
}
.fss-sig__save-default:disabled {
  background: #e9a992;
  border-color: #e9a992;
  color: #fff;
  cursor: not-allowed;
}

/*
  FS-045 — Metronic ตั้ง .alert เป็น display:flex (flex-wrap: nowrap)
  ลูกทุกตัวจึงกลายเป็น flex item แยกกัน ทั้ง <i>, <strong> และ text node
  ผลคือ <strong> ถูกบีบตามพื้นที่ที่เหลือ (วัดได้ 33.8px) แล้วตัดคำกลางคำ
  "ข้อสำคัญ" กลายเป็น "ข้อ" บรรทัดบน "สำคัญ" บรรทัดล่าง ทับกับข้อความถัดไป
  และช่องไฟระหว่างก้อนก็หายเพราะ flex ไม่มี white-space ระหว่าง item

  คืนให้ไหลเป็นข้อความปกติ — ไอคอนเป็น inline นำหน้าเหมือนที่ design ตั้งใจ
*/
.alert {
  display: block;
}

.alert > i {
  margin-right: 0.35rem;
}

/* design ข้อ 1 — หัว popup พื้นน้ำเงิน (โทน brand ของ Metronic) */
.fss-sig__header {
  background-color: #5d78ff;
  color: #ffffff;
}

.fss-sig__header .modal-title {
  color: #ffffff;
}

.fss-sig__header .close,
.fss-sig__header .close span {
  color: #ffffff;
  opacity: 0.85;
  text-shadow: none;
}

.fss-sig__header .close:hover {
  opacity: 1;
}

.fss-sig__header-doc {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
}

.fss-sig__alert {
  margin-bottom: 1rem;
}

.fss-sig__tabs {
  margin-bottom: 0;
}

.fss-sig__tab-badge {
  margin-left: 0.35rem;
  vertical-align: middle;
}

.fss-sig__content {
  border: 1px solid #ebedf2;
  border-top: 0;
  padding: 1.25rem;
}

.fss-sig__notice {
  margin-bottom: 1rem;
}

/* design ข้อ 4 — คำกำกับบน/ล่างกรอบ preview */
.fss-sig__frame-caption {
  font-size: 0.85rem;
  color: #74788d;
  margin-bottom: 0.35rem;
}

.fss-sig__zoom-note {
  color: #5d78ff;
}


.fss-sig__preview {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  border: 1px dashed #d1d7e2;
  border-radius: 4px;
  /* FS-113 — พื้นขาวแบบกระดาษ PDF (เดิมเป็นตารางหมากรุกไว้ดูความโปร่งใส) */
  background-color: #fff;
  padding: 0.75rem 0;
  overflow: hidden;
}

/* FS-113 — .fss-sig__image / __empty ย้ายไปอยู่ใน FormSummaryStockSignaturePdfPreview.vue */

/* design ข้อ 6 — กล่องข้อมูลไฟล์คอลัมน์ขวา */
.fss-sig__filecard {
  border: 1px solid #ebedf2;
  border-radius: 4px;
  padding: 0.85rem;
  height: 100%;
}

.fss-sig__filecard-title {
  font-weight: 500;
  color: #48465b;
  margin-bottom: 0.5rem;
}

.fss-sig__filename {
  word-break: break-all;
}

.fss-sig__meta {
  margin-top: 0.4rem;
  font-size: 0.9rem;
}

.fss-sig__delete {
  margin-top: 0.85rem;
  white-space: normal;
}

/* design ข้อ 9 — พื้นที่ลากไฟล์มาวาง (แพตเทิร์นเดียวกับ popup แนบไฟล์เอกสาร) */
.fss-sig__dropzone {
  margin-top: 1.25rem;
  border: 2px dashed #d1d7e2;
  border-radius: 4px;
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  background-color: #f7f8fa;
  transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;
}

.fss-sig__dropzone:hover {
  border-color: #5d78ff;
}

.fss-sig__dropzone--danger:hover {
  border-color: #fd397a;
}

.fss-sig__dropzone--over {
  border-color: #5d78ff;
  background-color: #eef1ff;
}

.fss-sig__dropzone--danger.fss-sig__dropzone--over {
  border-color: #fd397a;
  background-color: #ffeef4;
}

.fss-sig__dropzone--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.fss-sig__dropzone-icon {
  font-size: 2rem;
  color: #5d78ff;
  display: block;
  margin-bottom: 8px;
}

.fss-sig__dropzone--danger .fss-sig__dropzone-icon {
  color: #fd397a;
}

.fss-sig__dropzone-title {
  font-weight: 500;
  margin-bottom: 8px;
}

.fss-sig__rules {
  margin-top: 0.5rem;
  font-size: 0.9rem;
}

.fss-sig__lock-note {
  margin-top: 1rem;
  margin-bottom: 0;
  font-size: 0.9rem;
}

.fss-sig__readonly {
  margin-top: 0.75rem;
  font-size: 0.9rem;
}

.fss-sig__file {
  display: none;
}

/* design ข้อ 5 — ชั้นซ้อนขยายภาพ (ทำเองด้วย CSS ไม่เพิ่ม library ใหม่) */
.fss-sig__zoom {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1060;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.75);
  cursor: zoom-out;
}

.fss-sig__zoom-image {
  max-width: 92%;
  max-height: 82%;
  background-color: #ffffff;
  padding: 8px;
  border-radius: 4px;
}

.fss-sig__zoom-close {
  margin-top: 0.75rem;
  color: #ffffff;
  font-size: 0.9rem;
}
</style>



// WEBPACK FOOTER //
// App/Components/PrintSummary/FormSummaryStockSignatureModal.vue